/* ============================================================
   FIND NONNY store locator — companion to sections/find-nonny-map.liquid
   Data source: the server-rendered list items' data-* attributes
   (single source of truth on the page; no duplicate JSON payload).
   Map: vendored MapLibre GL (assets/find-nonny-maplibre.js) +
   OpenFreeMap vector tiles (free for commercial use, no API key),
   restyled at runtime into the Nonny palette below.
   Reviewed by adversarial panel 2026-07-09 — key behaviors:
   diacritic-folded tokenized search, UTM-preserving URL state,
   distance-mode header suppression, pending-fly queue for pre-load
   taps, index-safe pin lookup, late-load failure-note clearing.
   Audit fixes 2026-07-09: near-me keeps the distance-sorted LIST
   visible on mobile (the list IS the answer), invisible oversized
   hit layers for pins/clusters, post-load tile-error note, map
   reframe on chip tap + search commit, no silent auto-locate.
   Mobile redesign 2026-07-29 (Lane's phone review): LIST/MAP toggle
   replaced by a floating Map button opening a FULL-BLEED fixed map
   (one-finger pan there — cooperative gestures off while open, page
   scroll locked); detail card hides the button while open.
   Runbook: github.com/Nonnybeer/nonny-store-locator
   ============================================================ */
(function () {
  'use strict';

  var PALETTE = { // brand hexes (theme settings_data.json is canonical)
    cream: '#e2ddcb', creamLight: '#efe9d9', black: '#000000',
    yellow: '#ded154', red: '#dc3128', navy: '#003963', green: '#3c5429',
    water: '#c3cfd9', road: '#ffffff', label: '#3a3a3a'
  };
  // CA province + US state codes are disjoint, so one flat map serves both
  var REGIONS = {
    BC: 'British Columbia', AB: 'Alberta', SK: 'Saskatchewan', MB: 'Manitoba',
    ON: 'Ontario', QC: 'Quebec', NS: 'Nova Scotia', NB: 'New Brunswick',
    PE: 'Prince Edward Island', NL: 'Newfoundland', YT: 'Yukon', NT: 'Northwest Territories', NU: 'Nunavut',
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
    CT: 'Connecticut', DE: 'Delaware', DC: 'Washington DC', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii',
    ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky',
    LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
    MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire',
    NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
    OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
    SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
    WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming'
  };
  var MAP_TIMEOUT_MS = 15000;
  var HTTP_RE = /^https?:\/\//i;

  var root = document.querySelector('[data-fnm]');
  if (!root) return;

  var els = {
    q: root.querySelector('[data-fnm-q]'),
    near: root.querySelector('[data-fnm-near]'),
    azback: root.querySelector('[data-fnm-azback]'),
    controls: root.querySelector('[data-fnm-controls]'),
    chips: root.querySelectorAll('[data-fnm-chips] .fnm__chip'),
    prov: root.querySelector('[data-fnm-prov]'),
    count: root.querySelector('[data-fnm-count]'),
    fab: root.querySelector('[data-fnm-fab]'),
    fabLabel: root.querySelector('[data-fnm-fab-label]'),
    mapPanel: root.querySelector('[data-fnm-map-panel]'),
    mapEl: root.querySelector('[data-fnm-map]'),
    skeleton: root.querySelector('[data-fnm-skeleton]'),
    mapNote: root.querySelector('[data-fnm-map-note]'),
    list: root.querySelector('[data-fnm-list]'),
    empty: root.querySelector('[data-fnm-empty]'),
    emptyCta: root.querySelector('.fnm__empty-cta'),
    detail: root.querySelector('[data-fnm-detail]'),
    detailName: root.querySelector('[data-fnm-detail-name]'),
    detailAddr: root.querySelector('[data-fnm-detail-addr]'),
    detailDir: root.querySelector('[data-fnm-detail-dir]'),
    detailWeb: root.querySelector('[data-fnm-detail-web]'),
    detailTel: root.querySelector('[data-fnm-detail-tel]'),
    detailClose: root.querySelector('[data-fnm-detail-close]')
  };
  if (!els.list) return;

  root.style.setProperty('--fnm-map-h', root.getAttribute('data-map-height') || '60');

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  var market = root.getAttribute('data-market') || 'CA';
  var useMiles = market === 'US';

  function fold(s) {
    s = (s || '').toLowerCase();
    return typeof s.normalize === 'function' ? s.normalize('NFD').replace(/[̀-ͯ]/g, '') : s;
  }

  // chip data-type is a SET of type chars (e.g. "glx" = in-store); match on any overlap
  function typeHit(t, filter) {
    for (var i = 0; i < filter.length; i++) {
      if (t.indexOf(filter.charAt(i)) !== -1) return true;
    }
    return false;
  }

  /* ---------- data: read the SSR list ---------- */
  var TYPE_WORDS = { b: 'bar restaurant brewery pub', g: 'grocery grocer market',
                     l: 'liquor bottle shop', o: 'online', x: '' };
  var stores = [];
  els.list.querySelectorAll('[data-fnm-item]').forEach(function (li) {
    var lat = parseFloat(li.getAttribute('data-lat'));
    var lng = parseFloat(li.getAttribute('data-lng'));
    if (isNaN(lat) || isNaN(lng)) { li.classList.add('is-hidden'); return; } // malformed row: degrade invisibly
    var t = li.getAttribute('data-t') || 'x';
    var prov = li.getAttribute('data-prov') || '';
    var country = li.getAttribute('data-country') || 'CA';
    // haystack: SSR text + region/country names + type words, so
    // "liquor vancouver" or "whistler bc" match regardless of word order
    var extra = ' ' + prov + ' ' + (REGIONS[prov] || '') +
      (country === 'US' ? ' united states usa' : ' canada');
    for (var ci = 0; ci < t.length; ci++) extra += ' ' + (TYPE_WORDS[t.charAt(ci)] || '');
    stores.push({
      i: stores.length, el: li,
      name: (li.querySelector('.fnm__item-name') || {}).textContent || '',
      addr: (li.querySelector('.fnm__item-addr') || {}).textContent || '',
      lat: lat, lng: lng,
      t: t, prov: prov, country: country,
      web: li.getAttribute('data-web') || '',
      tel: li.getAttribute('data-tel') || '',
      search: fold(li.getAttribute('data-search') + extra),
      visible: true, dist: null
    });
  });

  /* ---------- province select ---------- */
  var provs = {};
  stores.forEach(function (s) { if (s.prov) provs[s.country + '|' + s.prov] = true; });
  Object.keys(provs).sort().forEach(function (key) {
    var parts = key.split('|');
    var opt = document.createElement('option');
    opt.value = parts[1];
    opt.textContent = (REGIONS[parts[1]] || parts[1]) + (parts[0] === 'US' ? ' (US)' : '');
    els.prov.appendChild(opt);
  });

  /* ---------- chip counts + aria ---------- */
  els.chips.forEach(function (chip) {
    chip.setAttribute('aria-pressed', chip.classList.contains('is-active') ? 'true' : 'false');
    var t = chip.getAttribute('data-type');
    if (!t) return;
    var n = stores.filter(function (s) { return typeHit(s.t, t); }).length;
    var span = document.createElement('span');
    span.className = 'fnm__chip-count';
    span.textContent = ' ' + n;
    chip.appendChild(span);
  });

  /* ---------- filter state ---------- */
  var params = new URLSearchParams(location.search);
  var state = { q: params.get('q') || '', type: params.get('type') || '', prov: params.get('prov') || '' };
  var distanceMode = false;
  if (els.q) els.q.value = state.q;
  if (state.prov) els.prov.value = state.prov;
  els.chips.forEach(function (c) {
    var active = (c.getAttribute('data-type') || '') === state.type;
    c.classList.toggle('is-active', active);
    c.setAttribute('aria-pressed', active ? 'true' : 'false');
  });

  function matches(s) {
    if (state.type && !typeHit(s.t, state.type)) return false;
    if (state.prov && s.prov !== state.prov) return false;
    if (state.q) {
      // every whitespace-separated token must appear somewhere (order-free)
      var toks = fold(state.q).trim().split(/\s+/);
      for (var i = 0; i < toks.length; i++) {
        if (toks[i] && s.search.indexOf(toks[i]) === -1) return false;
      }
    }
    return true;
  }

  function syncUrl() {
    var p = new URLSearchParams(location.search); // preserve utm_*, preview params, etc.
    ['q', 'type', 'prov'].forEach(function (k) { p.delete(k); });
    if (state.q) p.set('q', state.q);
    if (state.type) p.set('type', state.type);
    if (state.prov) p.set('prov', state.prov);
    var qs = p.toString();
    var next = location.pathname + (qs ? '?' + qs : '');
    if (next !== location.pathname + location.search) history.replaceState(null, '', next);
  }

  function applyFilters() {
    var shown = 0;
    stores.forEach(function (s) {
      s.visible = matches(s);
      s.el.classList.toggle('is-hidden', !s.visible);
      if (s.visible) shown++;
    });
    if (distanceMode) {
      // distance-sorted view: geographic headers are meaningless — keep them all hidden
      els.list.querySelectorAll('.fnm__group').forEach(function (g) { g.classList.add('is-hidden'); });
    } else {
      var pending = { country: null, prov: null, city: null };
      var rank = { country: 0, prov: 1, city: 2 };
      function flush(level) {
        ['city', 'prov', 'country'].forEach(function (l) {
          if (pending[l] && rank[l] >= rank[level]) { pending[l].classList.add('is-hidden'); pending[l] = null; }
        });
      }
      Array.prototype.forEach.call(els.list.children, function (el) {
        if (el.classList.contains('fnm__group--country')) { flush('country'); el.classList.remove('is-hidden'); pending.country = el; }
        else if (el.classList.contains('fnm__group--prov')) { flush('prov'); el.classList.remove('is-hidden'); pending.prov = el; }
        else if (el.classList.contains('fnm__group--city')) { flush('city'); el.classList.remove('is-hidden'); pending.city = el; }
        else if (el.classList.contains('fnm__item') && !el.classList.contains('is-hidden')) {
          pending.country = pending.prov = pending.city = null;
        }
      });
      flush('country');
    }
    if (els.count) {
      els.count.textContent = shown === stores.length
        ? stores.length + ' spots pouring Nonny'
        : shown + ' of ' + stores.length + ' spots';
    }
    if (els.empty) {
      els.empty.hidden = shown !== 0;
      if (shown === 0 && els.emptyCta) { // zero-result searches are a retail-demand signal
        var where = state.q || (state.prov ? (REGIONS[state.prov] || state.prov) : 'me');
        els.emptyCta.href = 'mailto:orders@nonny.beer?subject=' + encodeURIComponent('Stock Nonny near ' + where + '!');
      }
    }
    if (map && mapReady) refreshMapData();
    syncUrl();
  }

  /* ---------- events: search / chips / province ---------- */
  // reframe on explicit commits (Enter/blur/chip tap) — never on keystrokes
  function reframeMap() {
    if (map && mapReady) { fitToVisible(false); pendingFit = false; }
    else { pendingFit = true; pendingCamera = null; }
  }
  var qTimer, qCommitted = '';
  if (els.q) {
    els.q.addEventListener('input', function () {
      clearTimeout(qTimer);
      qTimer = setTimeout(function () { state.q = els.q.value; applyFilters(); }, 140);
    });
    els.q.addEventListener('keydown', function (e) { if (e.key === 'Enter') els.q.blur(); });
    els.q.addEventListener('blur', function () {
      state.q = els.q.value;
      if (state.q !== qCommitted) {
        qCommitted = state.q;
        applyFilters();
        if (state.q) reframeMap();
      }
    });
  }
  els.chips.forEach(function (chip) {
    chip.addEventListener('click', function () {
      state.type = chip.getAttribute('data-type') || '';
      els.chips.forEach(function (c) {
        var active = c === chip;
        c.classList.toggle('is-active', active);
        c.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      applyFilters();
      reframeMap();
    });
  });
  if (els.prov) els.prov.addEventListener('change', function () {
    state.prov = els.prov.value;
    applyFilters();
    // reframe the map on the chosen region (or back to the market default on "All")
    if (map && mapReady) { fitToVisible(false); pendingFit = false; }
    else { pendingFit = true; pendingCamera = null; }
  });

  /* ---------- near me ---------- */
  function haversineKm(aLat, aLng, bLat, bLng) {
    var R = 6371, dLat = (bLat - aLat) * Math.PI / 180, dLng = (bLng - aLng) * Math.PI / 180;
    var h = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(aLat * Math.PI / 180) * Math.cos(bLat * Math.PI / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.asin(Math.sqrt(h));
  }
  function distLabel(km) {
    if (useMiles) { var mi = km * 0.621371; return mi < 0.2 ? Math.round(mi * 5280) + ' ft away' : mi.toFixed(1) + ' mi away'; }
    return km < 1 ? Math.round(km * 1000) + ' m away' : km.toFixed(1) + ' km away';
  }
  // snapshot of the server-rendered list order (headers + items interleaved) so
  // distance mode is reversible — "Back to A–Z" re-appends in this exact order
  var originalOrder = Array.prototype.slice.call(els.list.children);
  var nearHTML = els.near ? els.near.innerHTML : '';
  var nearResetTimer;
  if (els.near) {
    els.near.addEventListener('click', function () {
      if (!('geolocation' in navigator)) return;
      clearTimeout(nearResetTimer);
      els.near.disabled = true; els.near.textContent = 'Locating…';
      navigator.geolocation.getCurrentPosition(function (pos) {
        els.near.disabled = false; els.near.innerHTML = nearHTML;
        var uLat = pos.coords.latitude, uLng = pos.coords.longitude;
        distanceMode = true;
        if (els.azback) els.azback.hidden = false;
        stores.forEach(function (s) { s.dist = haversineKm(uLat, uLng, s.lat, s.lng); });
        stores.slice().sort(function (a, b) { return a.dist - b.dist; }).forEach(function (s) {
          els.list.appendChild(s.el);
          var d = s.el.querySelector('[data-fnm-dist]');
          if (d) d.textContent = distLabel(s.dist);
        });
        applyFilters(); // hides all group headers in distanceMode
        // The distance-sorted LIST is the answer to "where's the closest?" —
        // never yank mobile users to the map. Camera still updates (queued if
        // needed) so the map is centred whenever they choose to open it.
        var cam = { center: [uLng, uLat], zoom: 11 };
        userMoved = true;
        if (root.getAttribute('data-view') === 'map' ||
            window.matchMedia('(min-width: 990px)').matches) {
          initMap();
          if (map && mapReady) map[reducedMotion ? 'jumpTo' : 'flyTo'](cam);
          else { pendingCamera = cam; pendingFit = false; }
        } else {
          pendingCamera = cam; pendingFit = false;
        }
        // surface the nearest results: scroll the list top under the controls
        var lr = els.list.getBoundingClientRect();
        var cb2 = els.controls ? els.controls.getBoundingClientRect().bottom : 0;
        if (lr.top < cb2 || lr.top > window.innerHeight * 0.6) {
          window.scrollTo({ top: window.scrollY + lr.top - cb2 - 8,
                            behavior: reducedMotion ? 'auto' : 'smooth' });
        }
      }, function () {
        els.near.disabled = false;
        els.near.textContent = 'Pick a province ↓';
        nearResetTimer = setTimeout(function () { els.near.innerHTML = nearHTML; }, 4000);
        if (els.prov) els.prov.focus();
      }, { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 });
    });
  }
  if (els.azback) {
    els.azback.addEventListener('click', function () {
      distanceMode = false;
      els.azback.hidden = true;
      originalOrder.forEach(function (el) { els.list.appendChild(el); });
      stores.forEach(function (s) {      // stale "2.3 km away" labels lie once order is A–Z
        s.dist = null;
        var d = s.el.querySelector('[data-fnm-dist]');
        if (d) d.textContent = '';
      });
      applyFilters(); // restores the geographic group headers
      if (window.matchMedia('(max-width: 989px)').matches) setView('list');
    });
  }

  /* ---------- full-bleed map behind the floating button (mobile) ---------- */
  function ensureMapVisible() {
    if (window.matchMedia('(max-width: 989px)').matches) setView('map');
  }
  function setCoopGestures(on) {
    // full-bleed map has no page to scroll — one-finger pan is what users expect
    try {
      if (map && map.cooperativeGestures) {
        map.cooperativeGestures[on ? 'enable' : 'disable']();
      }
    } catch (e) { /* handler API drift is cosmetic here */ }
  }
  function setView(v) {
    root.setAttribute('data-view', v);
    var mapOpen = v === 'map';
    if (els.fabLabel) els.fabLabel.textContent = mapOpen ? 'View list' : 'View map';
    if (els.fab) els.fab.setAttribute('aria-pressed', mapOpen ? 'true' : 'false');
    var mobile = window.matchMedia('(max-width: 989px)').matches;
    // lock the page behind the fixed overlay so iOS rubber-banding can't drag it
    document.documentElement.style.overflow = (mapOpen && mobile) ? 'hidden' : '';
    if (mapOpen) {
      initMap();
      setCoopGestures(!mobile);
      if (map && mapReady) requestAnimationFrame(function () { map.resize(); });
    } else {
      setCoopGestures(true);
    }
  }
  if (els.fab) {
    els.fab.addEventListener('click', function () {
      setView(root.getAttribute('data-view') === 'map' ? 'list' : 'map');
    });
  }

  /* ---------- detail card ---------- */
  var selectedEl = null, detailReturnFocus = null;
  function showDetail(s) {
    if (selectedEl) selectedEl.classList.remove('is-selected');
    s.el.classList.add('is-selected');
    selectedEl = s.el;
    detailReturnFocus = document.activeElement;
    els.detailName.textContent = s.name;
    els.detailAddr.textContent = s.addr;
    var dest = encodeURIComponent(s.lat + ',' + s.lng);
    els.detailDir.href = isIOS
      ? 'https://maps.apple.com/?daddr=' + dest
      : 'https://www.google.com/maps/dir/?api=1&destination=' + dest;
    var webOk = HTTP_RE.test(s.web);
    els.detailWeb.hidden = !webOk;
    if (webOk) els.detailWeb.href = s.web;
    els.detailTel.hidden = !s.tel;
    if (s.tel) { els.detailTel.href = 'tel:' + s.tel.replace(/[^+0-9]/g, ''); }
    els.detail.hidden = false;
    root.setAttribute('data-detail-open', ''); // CSS hides the floating Map button under the card
    if (els.detailClose) els.detailClose.focus({ preventScroll: true }); // :focus-visible keeps mouse taps ring-free
  }
  function closeDetail() {
    if (els.detail.hidden) return;
    els.detail.hidden = true;
    root.removeAttribute('data-detail-open');
    if (selectedEl) selectedEl.classList.remove('is-selected');
    if (detailReturnFocus && document.contains(detailReturnFocus)) {
      detailReturnFocus.focus({ preventScroll: true });
    }
  }
  if (els.detailClose) els.detailClose.addEventListener('click', closeDetail);
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeDetail(); });

  /* ---------- map ---------- */
  var map = null, mapReady = false, mapRequested = false, pendingCamera = null;
  var pendingFit = false;   // a filter change asked for a re-frame before the map loaded
  var userMoved = false;    // any user-driven camera intent — the silent auto-focus must never override it

  function flyOrQueue(camera) {
    userMoved = true;
    initMap();
    ensureMapVisible();
    if (map && mapReady) {
      map[reducedMotion ? 'jumpTo' : 'flyTo'](camera);
    } else {
      pendingCamera = camera; // applied in the map 'load' handler
      pendingFit = false;     // an explicit camera beats a queued re-frame
    }
  }

  stores.forEach(function (s) {
    var btn = s.el.querySelector('[data-fnm-item-btn]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      showDetail(s);
      flyOrQueue({ center: [s.lng, s.lat], zoom: 14 });
    });
  });

  function geojson() {
    return {
      type: 'FeatureCollection',
      features: stores.filter(function (s) { return s.visible; }).map(function (s) {
        return {
          type: 'Feature', id: s.i,
          geometry: { type: 'Point', coordinates: [s.lng, s.lat] },
          properties: { i: s.i, n: s.name }
        };
      })
    };
  }
  function refreshMapData() {
    var src = map.getSource('stores');
    if (src) src.setData(geojson());
  }

  function loadAsset(tag, attrs) {
    return new Promise(function (resolve, reject) {
      var el = document.createElement(tag);
      Object.keys(attrs).forEach(function (k) { el[k] = attrs[k]; });
      el.onload = resolve; el.onerror = reject;
      document.head.appendChild(el);
    });
  }

  function mapFailed() {
    if (mapReady) return;
    if (els.mapNote) els.mapNote.hidden = false;
    var p = els.skeleton && els.skeleton.querySelector('p');
    if (p) p.textContent = 'Map unavailable — the list has you covered.';
    // only pull the user away if they didn't deliberately choose the map view
    if (window.matchMedia('(max-width: 989px)').matches && root.getAttribute('data-view') !== 'map') setView('list');
  }

  function fitToVisible(instant) {
    var vis = stores.filter(function (s) { return s.visible; });
    // market-aware default frame: fit the visitor's own country when it has stores
    // (a province/state filter already narrows vis, so the subset is then a no-op)
    var home = market === 'US' ? 'US' : 'CA';
    var near = vis.filter(function (s) { return s.country === home; });
    if (near.length) vis = near;
    if (!vis.length || !map.getContainer().clientWidth) return; // hidden canvas: retry on resize
    var b = new maplibregl.LngLatBounds();
    vis.forEach(function (s) { b.extend([s.lng, s.lat]); });
    map.fitBounds(b, { padding: 48, maxZoom: 11, duration: instant ? 0 : 400 });
  }

  function brandBasemap() {
    try {
      map.getStyle().layers.forEach(function (layer) {
        var id = layer.id, type = layer.type;
        try {
          if (type === 'background') map.setPaintProperty(id, 'background-color', PALETTE.creamLight);
          else if (id.indexOf('water') !== -1 && type === 'fill') map.setPaintProperty(id, 'fill-color', PALETTE.water);
          else if ((id.indexOf('park') !== -1 || id.indexOf('landcover') !== -1 || id.indexOf('landuse') !== -1) && type === 'fill') map.setPaintProperty(id, 'fill-color', '#e0e2c8');
          else if (type === 'line' && (id.indexOf('road') !== -1 || id.indexOf('highway') !== -1 || id.indexOf('street') !== -1)) map.setPaintProperty(id, 'line-color', PALETTE.road);
          else if (type === 'symbol' && map.getPaintProperty(id, 'text-color')) map.setPaintProperty(id, 'text-color', PALETTE.label);
        } catch (e) { /* individual layer quirks are fine */ }
      });
    } catch (e) { /* restyle is cosmetic — never fatal */ }
  }

  function initMap() {
    if (mapRequested) return;
    mapRequested = true;
    var failTimer = setTimeout(mapFailed, MAP_TIMEOUT_MS);
    Promise.all([
      loadAsset('script', { src: window.FNM_MAPLIBRE_JS }),
      loadAsset('link', { rel: 'stylesheet', href: window.FNM_MAPLIBRE_CSS })
    ]).then(function () {
      if (!window.maplibregl) throw new Error('maplibre missing');
      map = new maplibregl.Map({
        container: els.mapEl,
        style: 'https://tiles.openfreemap.org/styles/positron',
        center: [-119.5, 50.0], zoom: 4.4,
        cooperativeGestures: window.matchMedia('(pointer: coarse)').matches,
        attributionControl: {} // responsive: expanded on desktop, compact on phones
      });
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }));
      map.on('load', function () {
        clearTimeout(failTimer);
        mapReady = true;
        if (els.mapNote) els.mapNote.hidden = true; // clear any premature failure note
        if (els.skeleton) els.skeleton.remove();
        brandBasemap();
        map.addSource('stores', { type: 'geojson', data: geojson(), cluster: true, clusterRadius: 46, clusterMaxZoom: 13 });
        map.addLayer({ id: 'clusters', type: 'circle', source: 'stores', filter: ['has', 'point_count'],
          paint: { 'circle-color': PALETTE.yellow, 'circle-stroke-color': PALETTE.black, 'circle-stroke-width': 2,
            'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 50, 26, 150, 32] } });
        map.addLayer({ id: 'cluster-count', type: 'symbol', source: 'stores', filter: ['has', 'point_count'],
          layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-size': 13, 'text-font': ['Noto Sans Bold'] },
          paint: { 'text-color': PALETTE.black } });
        map.addLayer({ id: 'pins', type: 'circle', source: 'stores', filter: ['!', ['has', 'point_count']],
          paint: { 'circle-color': PALETTE.red, 'circle-stroke-color': PALETTE.black, 'circle-stroke-width': 2, 'circle-radius': 8 } });
        // invisible oversized hit targets — visible marks stay compact, fingers get ~44px
        map.addLayer({ id: 'clusters-hit', type: 'circle', source: 'stores', filter: ['has', 'point_count'],
          paint: { 'circle-color': '#000', 'circle-opacity': 0,
            'circle-radius': ['step', ['get', 'point_count'], 24, 10, 26, 50, 30, 150, 34] } });
        map.addLayer({ id: 'pins-hit', type: 'circle', source: 'stores', filter: ['!', ['has', 'point_count']],
          paint: { 'circle-color': '#000', 'circle-opacity': 0, 'circle-radius': 22 } });
        // store names under the dots once zoomed to neighbourhood level (Lane 2026-07-29:
        // bare dots are ambiguous on mobile without the list for context). MapLibre's
        // collision detection hides overlapping labels automatically.
        map.addLayer({ id: 'pin-labels', type: 'symbol', source: 'stores',
          filter: ['!', ['has', 'point_count']], minzoom: 10,
          layout: { 'text-field': ['get', 'n'], 'text-font': ['Noto Sans Bold'],
            'text-size': 11, 'text-offset': [0, 1.1], 'text-anchor': 'top', 'text-max-width': 9 },
          paint: { 'text-color': PALETTE.black, 'text-halo-color': PALETTE.creamLight,
            'text-halo-width': 1.4 } });
        if (pendingCamera) {
          map.jumpTo(pendingCamera);
          pendingCamera = null;
        } else {
          fitToVisible(true); // pendingFit included: fits whatever is visible now
          if (!map.getContainer().clientWidth) {
            map.once('resize', function () { fitToVisible(true); }); // was hidden at load: fit when shown
          }
        }
        pendingFit = false;
        map.on('click', 'clusters-hit', function (e) {
          var f = e.features && e.features[0];
          if (!f) return;
          map.getSource('stores').getClusterExpansionZoom(f.properties.cluster_id).then(function (z) {
            map[reducedMotion ? 'jumpTo' : 'easeTo']({ center: f.geometry.coordinates, zoom: z + 0.5 });
          }).catch(function () {});
        });
        map.on('click', 'pins-hit', function (e) {
          var f = e.features && e.features[0];
          if (!f) return;
          var s = stores[f.properties.i]; // i == stores array position by construction
          if (s) showDetail(s);
        });
        ['clusters-hit', 'pins-hit'].forEach(function (l) {
          map.on('mouseenter', l, function () { map.getCanvas().style.cursor = 'pointer'; });
          map.on('mouseleave', l, function () { map.getCanvas().style.cursor = ''; });
        });
      });
      var postLoadErrors = 0;
      map.on('error', function () {
        // pre-load: the 15s timeout owns total failure. Post-load: repeated
        // tile/glyph errors mean a degraded map — say so, keep the list primary.
        if (!mapReady || ++postLoadErrors < 5) return;
        if (els.mapNote) els.mapNote.hidden = false;
      });
    }).catch(function () { clearTimeout(failTimer); mapRequested = false; mapFailed(); });
  }

  /* Location is fetched only on an explicit "Near me" tap — no silent
     auto-locate, even for previously-granted visitors (audit M-12: the surprise
     camera move + privacy story weren't worth the convenience). */

  /* lazy init: when the map panel nears the viewport (desktop shows it in layout; on mobile the toggle calls initMap directly) */
  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) { io.disconnect(); initMap(); } });
    }, { rootMargin: '300px' });
    io.observe(els.mapEl);
  } else {
    initMap();
  }

  applyFilters();
})();
